import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getServices, getDomains, getMonitoringConfig } from '../lib/api';
import { ArrowRight, CheckCircle, Clock, AlertTriangle, RefreshCw, Activity } from 'lucide-react';

// Total monitoring checks and which ones require explicit setup
const TOTAL_CHECKS = 7; // ssl, emailAuth, reputation, uptime, breach, ct, cdn
const CHECKS_NEEDING_SETUP = {
  uptime: { configKey: 'uptime', isConfigured: (cfg) => cfg?.uptime?.endpoints?.length > 0 }
};

const Dashboard = () => {
  const { user, profile, loading: authLoading } = useAuth();
  const [services, setServices] = useState([]);
  const [monitoringStats, setMonitoringStats] = useState(null); // { active, total, domain }
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);
  const navigate = useNavigate();
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    if (authLoading) return;
    fetchServiceData();
    return () => { mounted.current = false; };
  }, [user, authLoading]);

  const fetchServiceData = async () => {
    try {
      setConnectionError(false);
      if (!user) {
        if (mounted.current) { setServices([]); setLoading(false); }
        return;
      }

      const { services: data } = await getServices();
      if (!mounted.current) return;

      const mapped = (data || []).map(s => ({
        id: s.id,
        service_type: s.service_type,
        tier: s.tier,
        onboarding_status: s.onboarding_status || 'NOT_STARTED'
      }));
      setServices(mapped);

      // For completed Trust Monitoring, fetch config to determine active checks
      const trustService = mapped.find(s => s.service_type === 'TRUST_MONITORING' && s.onboarding_status === 'COMPLETED');
      if (trustService) {
        try {
          const { domains } = await getDomains();
          const verifiedDomain = (domains || []).find(d => d.verification_status === 'VERIFIED');
          if (verifiedDomain && mounted.current) {
            const { config } = await getMonitoringConfig(verifiedDomain.domain_name);
            // Count how many checks are active (don't need setup OR have been configured)
            let active = TOTAL_CHECKS;
            for (const [, setup] of Object.entries(CHECKS_NEEDING_SETUP)) {
              if (!setup.isConfigured(config || {})) active--;
            }
            if (mounted.current) setMonitoringStats({ active, total: TOTAL_CHECKS, domain: verifiedDomain.domain_name });
          }
        } catch (e) { /* non-critical, keep going */ }
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      if (mounted.current) { setConnectionError(true); setServices([]); }
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  const getStatusBadge = (status, serviceType) => {
    // For completed Trust Monitoring, show monitoring stats instead of "Completed"
    if (status === 'COMPLETED' && serviceType === 'TRUST_MONITORING' && monitoringStats) {
      const allActive = monitoringStats.active === monitoringStats.total;
      return (
        <span style={{
          background: allActive ? '#d1fae5' : '#fef3c7',
          color: allActive ? '#065f46' : '#92400e',
          padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600
        }}>
          {monitoringStats.active}/{monitoringStats.total} checks active
        </span>
      );
    }

    const styles = {
      NOT_STARTED: { bg: '#fef3c7', color: '#92400e', text: 'Not Started' },
      IN_PROGRESS: { bg: '#dbeafe', color: '#1e40af', text: 'In Progress' },
      COMPLETED: { bg: '#d1fae5', color: '#065f46', text: 'Active' }
    };
    const style = styles[status] || styles.NOT_STARTED;
    return (
      <span style={{ background: style.bg, color: style.color, padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600 }}>
        {style.text}
      </span>
    );
  };

  if (authLoading || loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="container" style={{ padding: '4rem 0' }}>
      <div style={{ marginBottom: '3rem' }}>
        <h1 className="hero-title" style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>
          Welcome back{profile?.full_name ? `, ${profile.full_name}` : ''}
        </h1>
        <p style={{ color: 'var(--gray)', fontSize: '1.1rem' }}>
          Manage your security and identity services
        </p>
      </div>

      {connectionError && (
        <div style={{ marginBottom: '2rem', padding: '1rem 1.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <AlertTriangle size={24} style={{ color: '#dc2626' }} />
            <div>
              <p style={{ fontWeight: 600, color: '#dc2626', marginBottom: '0.25rem' }}>Connection Issue</p>
              <p style={{ color: '#7f1d1d', fontSize: '0.875rem' }}>Unable to reach the API. Please check your connection.</p>
            </div>
          </div>
          <button onClick={fetchServiceData} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 500 }}>
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      )}

      {services.length === 0 && (
        <div style={{ marginBottom: '3rem' }}>
          <h2 className="section-title" style={{ marginBottom: '1.5rem' }}>Get Started</h2>
          <div className="grid-2">
            <div className="card" onClick={() => navigate('/dashboard/monitoring/onboarding')} style={{ cursor: 'pointer' }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ width: '48px', height: '48px', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>🛡️</span>
                </div>
                <h3 className="subsection-title">Trust Monitoring</h3>
                <p style={{ color: 'var(--gray)', marginBottom: '1rem' }}>Monitor SSL, email authentication, domain reputation, and more</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--gold)', fontWeight: 600 }}>
                Start Setup <ArrowRight size={20} />
              </div>
            </div>
            <div className="card" style={{ cursor: 'default', opacity: 0.85 }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ width: '48px', height: '48px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1rem' }}>
                  <span style={{ fontSize: '1.5rem' }}>🔐</span>
                </div>
                <h3 className="subsection-title">Identity Services</h3>
                <p style={{ color: 'var(--gray)', marginBottom: '1rem' }}>
                  Unlock enterprise-grade SSO, MFA, and structured IAM — available to subscribers of the Identity Services plan.
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--gray)', fontSize: '0.875rem', fontStyle: 'italic' }}>
                Available with an active Identity Services subscription
              </div>
            </div>
          </div>
        </div>
      )}

      {services.length > 0 && (
        <div>
          <h2 className="section-title" style={{ marginBottom: '1.5rem' }}>Your Services</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {services.map((service) => (
              <div key={service.id} className="card" style={{ cursor: 'pointer' }} onClick={() => {
                const isMonitoring = service.service_type === 'TRUST_MONITORING';
                if (service.onboarding_status !== 'COMPLETED') {
                  navigate(isMonitoring ? '/dashboard/monitoring/onboarding' : '/dashboard/identity/onboarding');
                } else {
                  navigate(isMonitoring ? '/dashboard/monitoring' : '/dashboard/identity');
                }
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                      <h3 className="subsection-title" style={{ marginBottom: 0 }}>
                        {service.service_type === 'TRUST_MONITORING' ? 'Trust Monitoring' : 'Identity Services'}
                      </h3>
                      {getStatusBadge(service.onboarding_status, service.service_type)}
                    </div>
                    <p style={{ color: 'var(--gray)', fontSize: '0.875rem' }}>
                      Tier: {service.tier}
                      {service.service_type === 'TRUST_MONITORING' && monitoringStats?.domain && service.onboarding_status === 'COMPLETED' && (
                        <span style={{ marginLeft: '1rem', fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.8rem', color: 'var(--charcoal)' }}>
                          {monitoringStats.domain}
                        </span>
                      )}
                    </p>
                    {service.service_type === 'TRUST_MONITORING' && monitoringStats && monitoringStats.active < monitoringStats.total && service.onboarding_status === 'COMPLETED' && (
                      <p style={{ color: 'var(--gold)', fontSize: '0.8rem', fontWeight: 500, marginTop: '0.35rem' }}>
                        {monitoringStats.total - monitoringStats.active} check{monitoringStats.total - monitoringStats.active > 1 ? 's' : ''} need{monitoringStats.total - monitoringStats.active === 1 ? 's' : ''} configuration
                      </p>
                    )}
                  </div>
                  <div>
                    {service.onboarding_status === 'COMPLETED' ? (
                      service.service_type === 'TRUST_MONITORING' && monitoringStats && monitoringStats.active < monitoringStats.total ? (
                        <Activity size={32} style={{ color: 'var(--gold)' }} />
                      ) : (
                        <CheckCircle size={32} style={{ color: 'var(--success)' }} />
                      )
                    ) : (
                      <Clock size={32} style={{ color: 'var(--gold)' }} />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '2rem', padding: '1.5rem', border: '2px dashed var(--gray)', borderRadius: '0.75rem', textAlign: 'center' }}>
            <p style={{ marginBottom: '1rem', color: 'var(--gray)' }}>Need another service?</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              {!services.find(s => s.service_type === 'TRUST_MONITORING') && (
                <button className="btn btn-secondary" onClick={() => navigate('/dashboard/monitoring/onboarding')}>Add Trust Monitoring</button>
              )}
              {!services.find(s => s.service_type === 'IDENTITY_SERVICES') && (
                <button className="btn btn-secondary" onClick={() => navigate('/dashboard/identity/onboarding')}>Add Identity Services</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
